import { useState, useEffect, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLocation } from "wouter";
import {
  Users, DollarSign, TrendingUp, LogOut, Building2, Calendar,
  Copy, Check, Target, CalendarClock, AlertTriangle, ChevronRight, ChevronLeft,
  Flame, Trophy, ArrowUpRight, ArrowDownRight, Minus,
  Medal, Crown, Star, Award, Zap,
  Layers, Hash, History, CheckCircle2, Shield, CreditCard, Clock, MapPin,
  Receipt, Search, ChevronDown, ChevronUp,
  ArrowRight, Loader2, AlertCircle,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { TrendLineChart, PeriodSelector, OnTrackBadge, SparklineChip, type TrendPeriod } from "@/components/trend-charts";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  Legend, ResponsiveContainer,
} from "recharts";

function fmt(d: string | null | undefined) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function money(value: number | null | undefined, digits = 2) {
  return value == null ? "Unavailable" : `$${value.toFixed(digits)}`;
}

function statusBadge(status: string | null | undefined) {
  if (status === "active") return <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 dark:border-green-800">Active</Badge>;
  if (status === "canceling") return <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800">Canceling</Badge>;
  if (status === "canceled") return <Badge variant="outline" className="text-muted-foreground">Cancelled</Badge>;
  return <Badge variant="outline" className="text-muted-foreground">Inactive</Badge>;
}

function DeltaBadge({ delta, testId }: { delta: number | null; testId?: string }) {
  if (delta === null) return <span className="text-xs text-muted-foreground" data-testid={testId}>—</span>;
  if (delta === 0) return <span className="flex items-center gap-0.5 text-xs text-muted-foreground" data-testid={testId}><Minus className="w-3 h-3" />0%</span>;
  if (delta > 0) return <span className="flex items-center gap-0.5 text-xs text-green-600 dark:text-green-400 font-medium" data-testid={testId}><ArrowUpRight className="w-3 h-3" />+{delta}%</span>;
  return <span className="flex items-center gap-0.5 text-xs text-red-500 dark:text-red-400 font-medium" data-testid={testId}><ArrowDownRight className="w-3 h-3" />{delta}%</span>;
}

type TierName = "Bronze" | "Silver" | "Gold" | "Platinum";

function tierStyle(tier: TierName) {
  switch (tier) {
    case "Platinum": return { bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-800", icon: Crown };
    case "Gold": return { bg: "bg-yellow-50 dark:bg-yellow-950/40", text: "text-yellow-700 dark:text-yellow-300", border: "border-yellow-200 dark:border-yellow-800", icon: Star };
    case "Silver": return { bg: "bg-slate-100 dark:bg-slate-800/60", text: "text-slate-600 dark:text-slate-300", border: "border-slate-300 dark:border-slate-600", icon: Medal };
    default: return { bg: "bg-orange-50 dark:bg-orange-950/40", text: "text-orange-700 dark:text-orange-300", border: "border-orange-200 dark:border-orange-800", icon: Award };
  }
}

function RankBadge({ rank, name }: { rank: number; name: string }) {
  const medals = ["🥇", "🥈", "🥉"];
  return (
    <span className="flex items-center gap-1.5 font-medium">
      {rank <= 3 ? <span className="text-base">{medals[rank - 1]}</span> : <span className="w-5 h-5 rounded-full bg-muted flex items-center justify-center text-xs font-bold text-muted-foreground">#{rank}</span>}
      <span className="truncate">{name}</span>
    </span>
  );
}

export default function SalespersonPage() {
  const [, navigate] = useLocation();
  const [copied, setCopied] = useState(false);
  const [leaderboardMetric, setLeaderboardMetric] = useState<"active_dealers" | "commission">("active_dealers");
  const { toast } = useToast();
  const qc = useQueryClient();

  // Bank account form state
  const [bankForm, setBankForm] = useState({
    accountHolderName: "",
    routingNumber: "",
    accountNumber: "",
    accountType: "checking" as "checking" | "savings",
  });
  const [bankFormEditing, setBankFormEditing] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState<"pending" | "all">("pending");
  const [trendPeriod, setTrendPeriod] = useState<TrendPeriod>("current");
  const [teamPeriod, setTeamPeriod] = useState<"current" | "3m" | "6m" | "ytd">("current");
  const [ledgerSearch, setLedgerSearch] = useState("");
  const [ledgerDateRange, setLedgerDateRange] = useState("all");
  const [ledgerSearchInput, setLedgerSearchInput] = useState("");
  const [clearanceSearch, setClearanceSearch] = useState("");
  const [clearanceSearchInput, setClearanceSearchInput] = useState("");
  const [clearancePeriod, setClearancePeriod] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [clearancePage, setClearancePage] = useState(0);
  const [clearancePageSize, setClearancePageSize] = useState(25);
  const [expandedLedgerRows, setExpandedLedgerRows] = useState<Set<string | number>>(new Set());
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerPageSize, setLedgerPageSize] = useState(25);

  useEffect(() => { setLedgerPage(0); }, [ledgerFilter, ledgerSearch, ledgerDateRange, ledgerPageSize]);

  useEffect(() => { setClearancePage(0); }, [clearanceSearch, clearancePeriod, clearancePageSize]);

  const { data, isLoading, isError } = useQuery<any>({
    queryKey: ["/api/salesperson/me"],
    queryFn: async () => (await apiRequest("POST", "/api/salesperson/me")).json(),
    retry: false,
  });

  const { data: dashboard } = useQuery<any>({
    queryKey: ["/api/salesperson/dashboard", trendPeriod],
    queryFn: async () => {
      const res = await apiRequest("POST", `/api/salesperson/dashboard?period=${trendPeriod}`);
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError,
  });

  const { data: leaderboard, isLoading: leaderboardLoading } = useQuery<any[]>({
    queryKey: ["/api/salesperson/leaderboard", leaderboardMetric],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/salesperson/leaderboard?metric=${leaderboardMetric}`);
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError,
    refetchInterval: leaderboardMetric === "commission" ? 30_000 : false,
  });

  const { data: paymentHistory = [], isLoading: paymentHistoryLoading } = useQuery<any[]>({
    queryKey: ["/api/salesperson/payment-history"],
    retry: false,
    enabled: !isLoading && !isError,
  });

  const { data: commissionLedger, isLoading: ledgerLoading } = useQuery<{
    entries: any[];
    unpaidTotal: number;
    kpi: {
      totalEarned: number;
      pendingTotal: number;
      paidTotal: number;
      thisMonthEarned: number;
      initialTotal: number;
      residualTotal: number;
      avgPayoutPerPeriod: number;
    };
  }>({
    queryKey: ["/api/salesperson/commission-ledger", ledgerFilter, ledgerSearch, ledgerDateRange],
    queryFn: async () => {
      const params = new URLSearchParams({ status: ledgerFilter, dateRange: ledgerDateRange });
      if (ledgerSearch) params.set("search", ledgerSearch);
      const res = await apiRequest("GET", `/api/salesperson/commission-ledger?${params}`);
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError,
    refetchInterval: 30_000,
  });

  const { data: clearanceSummary } = useQuery<{
    period: string;
    cleared: any[];
    pending: any[];
    totalEarned: number | null;
    kpi: { pendingCount: number; clearedCount: number; pendingAmount: number | null; clearedAmount: number | null };
  }>({
    queryKey: ["/api/salesperson/clearance-summary", clearanceSearch, clearancePeriod],
    queryFn: async () => {
      const params = new URLSearchParams({ period: clearancePeriod });
      if (clearanceSearch) params.set("search", clearanceSearch);
      const res = await apiRequest("GET", `/api/salesperson/clearance-summary?${params}`);
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError,
  });

  const allClearanceRows = clearanceSummary ? [...clearanceSummary.pending, ...clearanceSummary.cleared] : [];
  const clearanceTotalItems = allClearanceRows.length;
  const clearanceTotalPages = clearancePageSize === 0 ? 1 : Math.ceil(clearanceTotalItems / clearancePageSize);
  const clampedClearancePage = Math.min(clearancePage, Math.max(0, clearanceTotalPages - 1));
  const pagedClearanceRows = clearancePageSize === 0
    ? allClearanceRows
    : allClearanceRows.slice(clampedClearancePage * clearancePageSize, (clampedClearancePage + 1) * clearancePageSize);

  const { data: myTeam, isLoading: myTeamLoading, isError: myTeamError } = useQuery<any>({
    queryKey: ["/api/salesperson/my-team", teamPeriod],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/salesperson/my-team?period=${teamPeriod}`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message ?? "Failed to load team data");
      }
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError,
  });

  const { data: bankAccount, isLoading: bankAccountLoading } = useQuery<any>({
    queryKey: ["/api/salesperson/bank-account"],
    retry: false,
    enabled: !isLoading && !isError,
  });

  const { data: achPayoutHistory = [], isLoading: achHistoryLoading } = useQuery<any[]>({
    queryKey: ["/api/salesperson/ach-payout-history"],
    retry: false,
    enabled: !isLoading && !isError,
  });

  const saveBankAccount = useMutation({
    mutationFn: async (data: { accountHolderName: string; routingNumber: string; accountNumber: string; accountType: string }) => {
      const res = await apiRequest("POST", "/api/salesperson/bank-account", data);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: "Failed to save bank account" }));
        throw new Error(err.message ?? "Failed to save bank account");
      }
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["/api/salesperson/bank-account"] });
      setBankFormEditing(false);
      setBankForm({ accountHolderName: "", routingNumber: "", accountNumber: "", accountType: "checking" });
      toast({ title: "Bank account saved", description: "Your payment details have been saved securely." });
    },
    onError: (err: any) => {
      toast({ title: "Error saving bank account", description: err.message, variant: "destructive" });
    },
  });

  const { data: teamTrend } = useQuery<any>({
    queryKey: ["/api/salesperson/my-team/trend", teamPeriod],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/salesperson/my-team/trend?period=${teamPeriod}`);
      if (!res.ok) return null;
      return res.json();
    },
    retry: false,
    enabled: !isLoading && !isError && !!myTeam,
  });

  async function handleLogout() {
    await apiRequest("POST", "/api/auth/logout");
    window.location.href = "/login";
  }

  function copyCode(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Generate last 12 months (including current) for clearance period picker
  const clearancePeriodOptions: { value: string; label: string }[] = Array.from({ length: 12 }, (_, i) => {
    const d = new Date();
    d.setMonth(d.getMonth() - i);
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    return { value, label };
  });

  const tier: TierName = dashboard?.tier ?? "Bronze";
  const ts = tierStyle(tier);
  const TierIcon = ts.icon;
  const streakMonths: number = dashboard?.streakMonths ?? 0;
  const momDeltas = dashboard?.momDeltas ?? {};
  const chartData = dashboard?.chartData ?? [];
  const historicalChartData = dashboard?.historicalChartData ?? [];
  const velocityMessage: string = dashboard?.velocityMessage ?? "";
  const projectedCommission: number | null = dashboard?.projectedCommission ?? null;
  const recentWins: any[] = dashboard?.recentWins ?? [];
  const dealersSorted: any[] = dashboard?.dealersSorted ?? [];
  const isOnTrack: boolean | null = dashboard?.isOnTrack ?? null;
  const metricType: "dealer_count" | "mrr" = (data?.goalType === "mrr" ? "mrr" : "dealer_count");

  // Find this user's rank from the leaderboard (active_dealers default metric)
  const myLeaderboardRow = leaderboard?.find((r: any) => r.isCurrentUser);
  const myRank: number | null = myLeaderboardRow?.rank ?? null;

  const dealers = dealersSorted.length > 0 ? dealersSorted : (data?.dealers ?? []);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-background sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold text-lg">DealerMind</span>
            <Badge variant="secondary" className="text-xs">Sales Portal</Badge>

            {dashboard && (
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${ts.bg} ${ts.text} ${ts.border}`} data-testid="tier-badge">
                <TierIcon className="w-3.5 h-3.5" />
                {tier}
              </div>
            )}

            {streakMonths > 0 && (
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 dark:bg-orange-950/40 border border-orange-200 dark:border-orange-800 text-orange-700 dark:text-orange-300 text-xs font-semibold" data-testid="streak-counter">
                <Flame className="w-3.5 h-3.5" />
                {streakMonths} mo streak
              </div>
            )}

            {myRank !== null && (
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-xs font-semibold" data-testid="rank-badge">
                <Trophy className="w-3.5 h-3.5" />
                #{myRank}
              </div>
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="button-logout" className="gap-1.5">
            <LogOut className="w-4 h-4" />Log out
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">
        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-48 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : isError ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Unable to load your portal. Please try refreshing.
            </CardContent>
          </Card>
        ) : data && !data.dashboardPermissions?.dashboard_salesperson ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <Shield className="w-10 h-10 text-muted-foreground mx-auto" />
              <h2 className="text-lg font-semibold">Access Restricted</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                You don't have permission to view the salesperson dashboard. Please contact your administrator to be assigned a role.
              </p>
              <Button size="sm" variant="outline" onClick={handleLogout} data-testid="button-logout-no-access">
                <LogOut className="w-4 h-4 mr-1.5" />Sign out
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div>
              <h1 className="text-2xl font-bold mb-1">Welcome, {data.name ?? data.username}</h1>
              <p className="text-muted-foreground text-sm">Here's your performance summary</p>
            </div>

            {/* KPI stat cards with MoM deltas */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <Card data-testid="stat-total-dealers">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><Users className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground font-medium">Total Dealers</span></div>
                    <DeltaBadge delta={momDeltas.totalDealers ?? null} />
                  </div>
                  <p className="text-2xl font-bold">{data.totalDealers}</p>
                </CardContent>
              </Card>
              <Card data-testid="stat-active-dealers">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><Building2 className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground font-medium">Active Dealers</span></div>
                    <DeltaBadge delta={momDeltas.activeDealers ?? null} />
                  </div>
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{data.activeDealers}</p>
                </CardContent>
              </Card>
              <Card data-testid="stat-gross-mrr">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground font-medium">Gross MRR</span></div>
                    <DeltaBadge delta={momDeltas.grossMrr ?? null} />
                  </div>
                  <p className="text-2xl font-bold">{data.grossMrr == null ? "Unavailable" : `$${data.grossMrr.toFixed(2)}`}</p>
                </CardContent>
              </Card>
              <Card data-testid="stat-commission-due">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-muted-foreground" /><span className="text-xs text-muted-foreground font-medium">Commission Due</span></div>
                    <DeltaBadge delta={momDeltas.commissionDue ?? null} />
                  </div>
                  <p className="text-2xl font-bold text-primary">
                    {money(commissionLedger ? commissionLedger.unpaidTotal : data.commissionDue)}
                  </p>
                  {commissionLedger && (
                    <p className="text-xs text-muted-foreground mt-1">From payment ledger</p>
                  )}
                </CardContent>
              </Card>
              <Card data-testid="stat-net-to-business" className="border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20">
                <CardContent className="pt-5 pb-4">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-600 dark:text-emerald-400" /><span className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">Net to Business</span></div>
                  </div>
                  {(() => {
                    const commDue = commissionLedger ? commissionLedger.unpaidTotal : data.commissionDue;
                    const netToBusiness = data.grossMrr == null || commDue == null ? null : data.grossMrr - commDue;
                    const marginPct = data.grossMrr != null && commDue != null && data.grossMrr > 0
                      ? (commDue / data.grossMrr) * 100
                      : null;
                    return (
                      <>
                        <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">
                          {netToBusiness == null ? "Unavailable" : `$${netToBusiness.toFixed(2)}`}
                        </p>
                        <p className="text-xs text-emerald-600/70 dark:text-emerald-500/70 mt-1">
                          {marginPct == null ? "MRR unavailable" : `${(100 - marginPct).toFixed(1)}% net margin`}
                        </p>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>
            </div>

            {/* Goal trend chart + velocity + forecast */}
            {dashboard && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Trend chart */}
                <Card className="lg:col-span-2">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className="text-base font-semibold flex items-center gap-2">
                          <Target className="w-4 h-4" />Goal Trend
                        </CardTitle>
                        {data.goalType && data.goalValue && (
                          <Badge variant="outline" className="text-xs capitalize">{data.goalType === "dealer_count" ? "Dealer Count" : "MRR"} Goal</Badge>
                        )}
                        <OnTrackBadge isOnTrack={isOnTrack} />
                      </div>
                      <PeriodSelector value={trendPeriod} onChange={setTrendPeriod} />
                    </div>
                    <CardDescription className="text-xs">
                      {trendPeriod === "current" ? "Daily progress vs. goal this month" : "Monthly performance vs. goal"}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {(trendPeriod === "current" ? chartData : historicalChartData).length === 0 ? (
                      <p className="text-sm text-muted-foreground py-8 text-center">No trend data available.</p>
                    ) : (
                      <TrendLineChart
                        chartData={chartData}
                        historicalChartData={historicalChartData}
                        period={trendPeriod}
                        metric={metricType}
                        isOnTrack={isOnTrack}
                        height={200}
                      />
                    )}
                    {velocityMessage && trendPeriod === "current" && (
                      <div className="mt-3 flex items-start gap-2 bg-muted/40 rounded-lg px-3 py-2">
                        <Zap className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-muted-foreground" data-testid="text-velocity">{velocityMessage}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Forecast + streak */}
                <div className="flex flex-col gap-4">
                  {/* Commission forecast */}
                  <Card data-testid="card-commission-forecast">
                    <CardContent className="pt-5 pb-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium">Projected Earnings</span>
                      </div>
                      <p className="text-2xl font-bold text-primary">{projectedCommission == null ? "Unavailable" : `$${projectedCommission.toFixed(2)}`}</p>
                      <p className="text-xs text-muted-foreground">
                        {trendPeriod === "current"
                          ? "Estimated by month end at current pace"
                          : trendPeriod === "3m"
                          ? "Projected for current month (3M view)"
                          : trendPeriod === "6m"
                          ? "Projected for current month (6M view)"
                          : "Projected for current month (YTD view)"}
                      </p>
                      {data.commissionFormula && (
                        <p className="text-xs text-muted-foreground bg-muted/50 rounded p-2 mt-1 line-clamp-2" data-testid="text-commission-formula">
                          {data.commissionFormula}
                        </p>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                        <CalendarClock className="w-3.5 h-3.5" />
                        <span>Next payout: <strong className="text-foreground">{data.nextPayoutDate}</strong></span>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Streak counter */}
                  <Card data-testid="card-streak">
                    <CardContent className="pt-5 pb-4 space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        {streakMonths >= 3 ? <Trophy className="w-4 h-4 text-yellow-500" /> : <Flame className="w-4 h-4 text-orange-500" />}
                        <span className="text-sm font-medium">Quota Streak</span>
                      </div>
                      <p className="text-2xl font-bold">{streakMonths} <span className="text-sm font-normal text-muted-foreground">months</span></p>
                      <p className="text-xs text-muted-foreground">
                        {streakMonths === 0 ? "Hit your quota this month to start your streak!" :
                         streakMonths === 1 ? "1 consecutive month hitting quota — keep it up!" :
                         `${streakMonths} consecutive months hitting quota!`}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Tier progress */}
                  <Card data-testid="card-tier">
                    <CardContent className="pt-5 pb-4 space-y-2">
                      <div className={`flex items-center gap-2 mb-1`}>
                        <TierIcon className={`w-4 h-4 ${ts.text}`} />
                        <span className="text-sm font-medium">Your Tier</span>
                      </div>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-sm font-bold ${ts.bg} ${ts.text} ${ts.border} border`}>
                        <TierIcon className="w-3.5 h-3.5" />
                        {tier}
                      </div>
                      {dashboard.tierThresholds && (
                        <p className="text-xs text-muted-foreground">
                          Based on ${dashboard.cumulativeAttributedMrr?.toLocaleString() ?? 0} cumulative attributed MRR
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* Payment Clearance Summary */}
            {clearanceSummary && (
              <Card data-testid="card-clearance-summary">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <Shield className="w-4 h-4 text-primary" />Payment Clearance
                    </CardTitle>
                    <div className="flex items-center gap-2 flex-wrap">
                      {clearanceSummary.kpi && clearanceSummary.kpi.pendingCount > 0 && (
                        <Badge className="text-xs bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 gap-1">
                          <Clock className="w-3 h-3" />{clearanceSummary.kpi.pendingCount} Pending • {clearanceSummary.kpi.pendingAmount == null ? "Unavailable" : `$${clearanceSummary.kpi.pendingAmount.toFixed(0)}`} at stake
                        </Badge>
                      )}
                      {clearanceSummary.kpi && clearanceSummary.kpi.clearedCount > 0 && (
                        <Badge className="text-xs bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 gap-1">
                          <Check className="w-3 h-3" />{clearanceSummary.kpi.clearedCount} Cleared • {clearanceSummary.kpi.clearedAmount == null ? "Unavailable" : `$${clearanceSummary.kpi.clearedAmount.toFixed(0)}`}
                        </Badge>
                      )}
                    </div>
                  </div>
                  <CardDescription className="text-xs">
                    Subscription payment clearing status. Commissions release after all payments clear.
                  </CardDescription>
                  {/* Period picker + Search + Page size */}
                  <div className="flex gap-2 mt-2 flex-wrap">
                    <Select value={clearancePeriod} onValueChange={setClearancePeriod} data-testid="select-clearance-period">
                      <SelectTrigger className="h-8 text-xs w-44" data-testid="select-trigger-clearance-period">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {clearancePeriodOptions.map(o => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="relative flex-1 min-w-32">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                      <Input
                        className="pl-8 h-8 text-xs"
                        placeholder="Search dealer…"
                        value={clearanceSearchInput}
                        onChange={e => setClearanceSearchInput(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") setClearanceSearch(clearanceSearchInput); }}
                        data-testid="input-clearance-search"
                      />
                    </div>
                    <Select value={String(clearancePageSize)} onValueChange={v => setClearancePageSize(Number(v))}>
                      <SelectTrigger className="h-8 text-xs w-24" data-testid="select-clearance-page-size">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[{label:"25",value:25},{label:"50",value:50},{label:"75",value:75},{label:"All",value:0}].map(o => (
                          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  {clearanceSummary.pending.length === 0 && clearanceSummary.cleared.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-6 py-4">No dealers matched.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b bg-muted/40">
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Dealer</th>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs hidden md:table-cell">Method</th>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Payment Date</th>
                            <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs hidden lg:table-cell">Clears On</th>
                            <th className="text-right px-4 py-2.5 font-medium text-muted-foreground text-xs">Commission</th>
                            <th className="text-center px-4 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pagedClearanceRows.map((d: any, i: number) => {
                            const isPending = clearanceSummary.pending.includes(d);
                            return (
                              <Fragment key={i}>
                                <tr className="border-b hover:bg-muted/30 transition-colors" data-testid={`sp-clearance-row-${i}`}>
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">{d.dealerName}</span>
                                      {d.isCanceling && (
                                        <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 text-[10px] px-1.5 h-4">Canceling</Badge>
                                      )}
                                    </div>
                                  </td>
                                  <td className="px-4 py-2.5 hidden md:table-cell">
                                    {d.paymentMethod === "bank_account"
                                      ? <Badge variant="outline" className="text-[10px] gap-1 px-1.5 h-5"><Building2 className="w-2.5 h-2.5" />ACH</Badge>
                                      : d.paymentMethod === "card"
                                      ? <Badge variant="outline" className="text-[10px] gap-1 px-1.5 h-5"><CreditCard className="w-2.5 h-2.5" />Card</Badge>
                                      : <span className="text-xs text-muted-foreground">—</span>}
                                  </td>
                                  <td className="px-4 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                                    {d.pipeline?.paymentDate ?? (d.paymentDate ? d.paymentDate : "Awaiting")}
                                  </td>
                                  <td className="px-4 py-2.5 text-xs text-muted-foreground hidden lg:table-cell">
                                    {d.pipeline?.clearsOn ?? d.expectedClearDate ?? (isPending ? "Pending" : d.clearedDate ?? "—")}
                                  </td>
                                  <td className="px-4 py-2.5 text-right">
                                    <span className={`font-semibold text-sm ${isPending ? "text-yellow-600 dark:text-yellow-400" : "text-green-600 dark:text-green-400"}`}>
                          {d.commissionAtStake == null ? "—" : `$${d.commissionAtStake.toFixed(2)}`}
                                    </span>
                                  </td>
                                  <td className="px-4 py-2.5 text-center">
                                    {isPending ? (
                                      <Badge className="text-[10px] bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 gap-1 px-1.5 h-5">
                                        <Clock className="w-2.5 h-2.5" />Pending
                                      </Badge>
                                    ) : (
                                      <Badge className="text-[10px] bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 gap-1 px-1.5 h-5">
                                        <Check className="w-2.5 h-2.5" />Cleared
                                      </Badge>
                                    )}
                                  </td>
                                </tr>
                                {/* Pipeline row */}
                                {d.pipeline && (
                                  <tr className="border-b bg-muted/10" data-testid={`sp-clearance-pipeline-${i}`}>
                                    <td colSpan={6} className="px-4 py-1.5">
                                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground">
                                        {isPending ? (
                                          <>
                                            <span>Paid {d.pipeline.paymentDate}</span>
                                            <ArrowRight className="w-2.5 h-2.5 shrink-0" />
                                            <span>Clears ~{d.pipeline.clearsOn}</span>
                                            <ArrowRight className="w-2.5 h-2.5 shrink-0" />
                                            <span className="font-medium text-foreground">Payout ~{d.pipeline.nextPayoutWindow} ({d.pipeline.payFrequency})</span>
                                          </>
                                        ) : (
                                          <>
                                            <Check className="w-2.5 h-2.5 text-green-600 dark:text-green-400 shrink-0" />
                                            <span>Cleared {d.clearedDate ?? d.pipeline.clearsOn}</span>
                                            <ArrowRight className="w-2.5 h-2.5 shrink-0" />
                                            <span className="font-medium text-foreground">Payout ~{d.pipeline.nextPayoutWindow} ({d.pipeline.payFrequency})</span>
                                          </>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {clearanceTotalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 gap-2 flex-wrap text-xs text-muted-foreground border-t">
                      <span data-testid="clearance-pagination-info">
                        {clearancePageSize === 0 ? `All ${clearanceTotalItems}` : `${clampedClearancePage * clearancePageSize + 1}–${Math.min((clampedClearancePage + 1) * clearancePageSize, clearanceTotalItems)} of ${clearanceTotalItems}`}
                        {clearanceTotalPages > 1 && <> · Page {clampedClearancePage + 1} of {clearanceTotalPages}</>}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm" variant="outline" className="h-7 text-xs px-2 gap-1"
                          disabled={clampedClearancePage === 0}
                          onClick={() => setClearancePage(p => Math.max(0, p - 1))}
                          data-testid="clearance-pagination-prev"
                        >
                          <ChevronLeft className="w-3 h-3" />Previous
                        </Button>
                        <Button
                          size="sm" variant="outline" className="h-7 text-xs px-2 gap-1"
                          disabled={clampedClearancePage >= clearanceTotalPages - 1}
                          onClick={() => setClearancePage(p => Math.min(clearanceTotalPages - 1, p + 1))}
                          data-testid="clearance-pagination-next"
                        >
                          Next<ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Leaderboard */}
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Trophy className="w-4 h-4" />Leaderboard
                  </CardTitle>
                  <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-1" data-testid="leaderboard-toggle">
                    <Button
                      size="sm" variant={leaderboardMetric === "active_dealers" ? "default" : "ghost"}
                      className="h-7 text-xs px-3" onClick={() => setLeaderboardMetric("active_dealers")}
                      data-testid="button-leaderboard-dealers">
                      Active Dealers
                    </Button>
                    <Button
                      size="sm" variant={leaderboardMetric === "commission" ? "default" : "ghost"}
                      className="h-7 text-xs px-3" onClick={() => setLeaderboardMetric("commission")}
                      data-testid="button-leaderboard-commission">
                      Commission Earned
                    </Button>
                  </div>
                </div>
                <CardDescription className="text-xs">Top 5 salespeople this period. Peers outside top 5 are anonymized.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {leaderboardLoading ? (
                  <div className="px-6 pb-6 space-y-3 pt-2">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                  </div>
                ) : !leaderboard || leaderboard.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-6 pb-6">No leaderboard data yet.</p>
                ) : (
                  <div className="divide-y">
                    {leaderboard.map((row) => (
                      <div
                        key={row.rank}
                        className={`flex items-center justify-between px-6 py-3 ${row.isCurrentUser ? "bg-primary/5 border-l-2 border-primary" : ""}`}
                        data-testid={`leaderboard-row-${row.rank}`}
                      >
                        <RankBadge rank={row.rank} name={row.displayName} />
                        <div className="flex items-center gap-4 text-sm">
                          {row.isCurrentUser && <Badge variant="secondary" className="text-xs">You</Badge>}
                          <span className="text-muted-foreground text-xs">{row.activeDealers} active</span>
                          <span className="font-semibold tabular-nums">
                            {leaderboardMetric === "commission"
                              ? money(row.commissionEarned, 0)
                              : row.activeDealers}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Two-column: Recent wins + Referral code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Recent wins */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Star className="w-4 h-4 text-yellow-500" />Recent Wins
                  </CardTitle>
                  <CardDescription className="text-xs">Your latest dealer activations</CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {recentWins.length === 0 ? (
                    <p className="text-sm text-muted-foreground px-6 pb-6">No recent activations yet.</p>
                  ) : (
                    <div className="divide-y">
                      {recentWins.map((win) => (
                        <button
                          key={win.id}
                          onClick={() => navigate(`/salesperson/dealer/${win.id}`)}
                          className="w-full flex items-center justify-between gap-4 px-6 py-2.5 hover:bg-muted/30 transition-colors text-left group"
                          data-testid={`recent-win-${win.id}`}
                        >
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{win.name}</p>
                            <p className="text-xs text-muted-foreground">{fmt(win.date)}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-sm font-semibold text-green-600 dark:text-green-400">{win.mrr == null ? "Unavailable" : `$${win.mrr.toFixed(2)}/mo`}</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Referral code */}
              <Card>
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Your Referral Code</span>
                  </div>
                  {data.referralCode ? (
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xl font-bold tracking-wider text-primary" data-testid="text-referral-code">
                        {data.referralCode}
                      </span>
                      <Button size="sm" variant="outline" className="gap-1.5 h-7 text-xs"
                        onClick={() => copyCode(data.referralCode)} data-testid="button-copy-referral-code">
                        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {copied ? "Copied!" : "Copy"}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No referral code set. Contact your administrator to assign one.</p>
                  )}
                  {data.referralCode && (
                    <p className="text-xs text-muted-foreground">
                      Dealers who sign up with this code will be automatically attributed to you.
                    </p>
                  )}
                  {data.nextPayoutDate && (
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <CalendarClock className="w-3.5 h-3.5 shrink-0" />
                        <span>Next payout: <strong className="text-foreground">{data.nextPayoutDate}</strong></span>
                        {data.daysUntilPayout !== undefined && (
                          <Badge variant="outline" className="text-xs">{data.daysUntilPayout}d away</Badge>
                        )}
                        <Badge variant="outline" className="capitalize text-xs">{data.effectivePayFrequency ?? data.payFrequency ?? "monthly"}</Badge>
                      </div>
                      {data.usingGlobalDefault && (
                        <p className="text-[10px] text-muted-foreground pl-5">Using platform default schedule</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Goal progress (simple progress bar) */}
            {data.goalProgress && (
              <Card>
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Target className="w-4 h-4 text-primary" />
                      <span className="text-sm font-medium">Goal Progress</span>
                    </div>
                    <span className="text-sm font-bold">
                      {data.goalType === "dealer_count"
                        ? `${data.goalProgress.current} / ${data.goalProgress.target} dealers`
                         : data.goalProgress.current == null
                           ? "Unavailable"
                           : `$${data.goalProgress.current.toFixed(0)} / $${data.goalProgress.target.toFixed(0)} MRR`}
                    </span>
                  </div>
                  <Progress value={data.goalProgress.percentage} className="h-3" data-testid="progress-goal" />
                  <p className="text-xs text-muted-foreground">{data.goalProgress.percentage}% of goal reached</p>
                </CardContent>
              </Card>
            )}

            {/* Your Team */}
            {data.teamInfo && (
              <Card data-testid="card-team-info">
                <CardContent className="pt-5 pb-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <Layers className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Your Team</span>
                  </div>
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <p className="text-lg font-bold" data-testid="text-team-name">{data.teamInfo.teamName}</p>
                      {data.teamInfo.leaderUsername && (
                        <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
                          <Crown className="w-3.5 h-3.5 text-yellow-500" />
                          <span>Leader: <strong className="text-foreground">{data.teamInfo.leaderUsername}</strong></span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4">
                      {data.teamInfo.teamRank && (
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Team Rank</p>
                          <p className="text-xl font-bold text-primary" data-testid="text-team-rank">#{data.teamInfo.teamRank}</p>
                        </div>
                      )}
                      {data.teamInfo.orgRank && (
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground">Org Rank</p>
                          <p className="text-xl font-bold" data-testid="text-org-rank">#{data.teamInfo.orgRank}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* My Dealers — drill-down section */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="w-4 h-4" />My Dealers
                </CardTitle>
                <CardDescription className="text-xs">Canceling dealers sorted to top. Click any dealer to view their dashboard.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {dealers.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-6 pb-6">No dealers attributed yet.</p>
                ) : (
                  <div className="divide-y">
                    {dealers.map((dealer: any) => {
                      const isCanceling = dealer.subscriptionStatus === "canceling";
                      const isChurnRisk = dealer.isChurnRisk ?? isCanceling;
                      const isCanceled = !["active", "canceling"].includes(dealer.subscriptionStatus ?? "");
                      const mrr = dealer.currentMrr;
                      return (
                        <button
                          key={dealer.id}
                          onClick={() => navigate(`/salesperson/dealer/${dealer.id}`)}
                          className={`w-full flex items-center justify-between gap-4 px-6 py-3 hover:bg-muted/30 transition-colors text-left group ${isChurnRisk ? "bg-red-50/50 dark:bg-red-950/20" : ""}`}
                          data-testid={`dealer-row-${dealer.id}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center shrink-0 ${isChurnRisk ? "bg-red-100 dark:bg-red-900/30" : "bg-muted"}`}>
                              <span className={`text-xs font-semibold ${isChurnRisk ? "text-red-600 dark:text-red-400" : "text-muted-foreground"}`}>
                                {(dealer.companyName ?? dealer.username).slice(0, 2).toUpperCase()}
                              </span>
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{dealer.companyName ?? dealer.username}</p>
                              {dealer.onboardDate && (
                                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <Calendar className="w-3 h-3" />Onboarded {fmt(dealer.onboardDate)}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {isChurnRisk && (
                              <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 dark:border-red-800 gap-1 text-xs" data-testid={`churn-risk-${dealer.id}`}>
                                <AlertTriangle className="w-3 h-3" />Churn Risk
                              </Badge>
                            )}
                            {statusBadge(dealer.subscriptionStatus)}
                            <span className="text-sm font-semibold tabular-nums" data-testid={`dealer-mrr-${dealer.id}`}>
                              {mrr != null ? `$${Number(mrr).toFixed(2)}/mo` : <span className="text-muted-foreground">Unavailable</span>}
                            </span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission Ledger */}
            <Card data-testid="card-commission-ledger">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Receipt className="w-4 h-4" />Commission Ledger
                  </CardTitle>
                  <div className="flex items-center gap-1 bg-muted/60 rounded-lg p-1" data-testid="ledger-filter-toggle">
                    <Button
                      size="sm" variant={ledgerFilter === "pending" ? "default" : "ghost"}
                      className="h-7 text-xs px-3"
                      onClick={() => setLedgerFilter("pending")}
                      data-testid="button-ledger-filter-pending"
                    >Pending</Button>
                    <Button
                      size="sm" variant={ledgerFilter === "all" ? "default" : "ghost"}
                      className="h-7 text-xs px-3"
                      onClick={() => setLedgerFilter("all")}
                      data-testid="button-ledger-filter-all"
                    >All</Button>
                  </div>
                </div>
                <CardDescription className="text-xs">
                  Commission earned from dealer invoice payments.
                </CardDescription>

                {/* KPI row */}
                {commissionLedger?.kpi && (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mt-3" data-testid="ledger-kpi-row">
                    <div className="rounded-lg border bg-muted/20 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Total</p>
                      <p className="text-sm font-bold tabular-nums" data-testid="kpi-total-earned">${commissionLedger.kpi.totalEarned.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-yellow-50 dark:bg-yellow-950/20 border-yellow-200 dark:border-yellow-800 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-yellow-700 dark:text-yellow-400 uppercase tracking-wide">Pending</p>
                      <p className="text-sm font-bold tabular-nums text-yellow-700 dark:text-yellow-400" data-testid="kpi-pending">${commissionLedger.kpi.pendingTotal.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-green-700 dark:text-green-400 uppercase tracking-wide">Paid</p>
                      <p className="text-sm font-bold tabular-nums text-green-700 dark:text-green-400" data-testid="kpi-paid">${commissionLedger.kpi.paidTotal.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/20 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">This Month</p>
                      <p className="text-sm font-bold tabular-nums" data-testid="kpi-this-month">${commissionLedger.kpi.thisMonthEarned.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/20 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Initial</p>
                      <p className="text-sm font-bold tabular-nums" data-testid="kpi-initial">${commissionLedger.kpi.initialTotal.toFixed(2)}</p>
                    </div>
                    <div className="rounded-lg border bg-muted/20 p-2.5 space-y-0.5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Residual</p>
                      <p className="text-sm font-bold tabular-nums" data-testid="kpi-residual">${commissionLedger.kpi.residualTotal.toFixed(2)}</p>
                    </div>
                  </div>
                )}
                {/* Avg payout prominence */}
                {commissionLedger?.kpi && commissionLedger.kpi.avgPayoutPerPeriod > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
                    <CalendarClock className="w-3.5 h-3.5" />
                    Avg payout: <span className="font-semibold text-foreground" data-testid="kpi-avg-payout">${commissionLedger.kpi.avgPayoutPerPeriod.toFixed(2)}</span> per pay window
                  </div>
                )}

                {/* Search + date range filter */}
                <div className="flex gap-2 mt-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <Input
                      className="pl-8 h-8 text-xs"
                      placeholder="Search dealer…"
                      value={ledgerSearchInput}
                      onChange={e => setLedgerSearchInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") setLedgerSearch(ledgerSearchInput); }}
                      data-testid="input-ledger-search"
                    />
                  </div>
                  <Select value={ledgerDateRange} onValueChange={v => setLedgerDateRange(v)} data-testid="select-ledger-date-range">
                    <SelectTrigger className="h-8 text-xs w-40" data-testid="select-trigger-ledger-date-range">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="current_month">This Month</SelectItem>
                      <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                      <SelectItem value="last_12_months">Last 12 Months</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={String(ledgerPageSize)} onValueChange={v => setLedgerPageSize(Number(v))} data-testid="select-ledger-page-size">
                    <SelectTrigger className="h-8 text-xs w-20" data-testid="select-trigger-ledger-page-size">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="75">75</SelectItem>
                      <SelectItem value="0">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {ledgerLoading ? (
                  <div className="px-6 pb-6 space-y-2 pt-2">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
                ) : !commissionLedger || commissionLedger.entries.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-6 pb-6 pt-2">
                    {ledgerFilter === "pending"
                      ? "No pending commission entries. Commission entries appear here when your dealers' invoices are paid."
                      : "No commission entries yet. Commission entries appear here when your dealers' invoices are paid."}
                  </p>
                ) : (() => {
                  const allEntries: any[] = commissionLedger.entries;
                  const totalLedgerItems = allEntries.length;
                  const ledgerTotalPages = ledgerPageSize === 0 ? 1 : Math.ceil(totalLedgerItems / ledgerPageSize);
                  const clampedLedgerPage = Math.min(ledgerPage, Math.max(0, ledgerTotalPages - 1));
                  const pagedEntries: any[] = ledgerPageSize === 0 ? allEntries : allEntries.slice(clampedLedgerPage * ledgerPageSize, (clampedLedgerPage + 1) * ledgerPageSize);
                  const ledgerStart = ledgerPageSize === 0 ? 1 : clampedLedgerPage * ledgerPageSize + 1;
                  const ledgerEnd = ledgerPageSize === 0 ? totalLedgerItems : Math.min((clampedLedgerPage + 1) * ledgerPageSize, totalLedgerItems);
                  return (
                  <div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground">Type</th>
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground">Dealer</th>
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Date</th>
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Rate</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground hidden md:table-cell">Invoice</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Commission</th>
                          <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                          <th className="w-8 px-2 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedEntries.map((entry: any, _pagedIdx: number) => {
                          const isExpanded = expandedLedgerRows.has(entry.id);
                          const toggleExpand = () => setExpandedLedgerRows(prev => {
                            const next = new Set(prev);
                            if (next.has(entry.id)) next.delete(entry.id); else next.add(entry.id);
                            return next;
                          });
                          return (
                            <Fragment key={entry.id}>
                              <tr className={`border-b hover:bg-muted/20 transition-colors ${entry.residualCapWarning === "exhausted" ? "opacity-50" : ""}`} data-testid={`ledger-row-${entry.id}`}>
                                <td className="px-4 py-2.5 font-medium">
                                  <div className="flex flex-col gap-0.5">
                                    <span>{entry.dealerName}</span>
                                    <div className="flex items-center gap-1.5">
                                      {(entry.entryType ?? "personal") === "team_override" ? (
                                        <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 px-1.5 h-4" data-testid={`badge-ledger-type-${entry.id}`}>Team Override</Badge>
                                      ) : null}
                                      {entry.monthNumber && (
                                        <span className="text-[10px] text-muted-foreground">Month {entry.monthNumber}</span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 text-muted-foreground text-xs hidden md:table-cell">
                                  {entry.createdAt ? new Date(entry.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                                </td>
                                <td className="px-4 py-2.5 hidden md:table-cell">
                                  {entry.rateApplied ? (
                                    <Badge variant="outline" className={`text-[10px] px-1.5 h-5 ${entry.rateType === "initial" || entry.rateType === "one-time" ? "border-blue-200 text-blue-700 dark:text-blue-400" : "border-muted text-muted-foreground"}`}>
                                      {entry.rateApplied}
                                    </Badge>
                                  ) : <span className="text-muted-foreground text-xs">—</span>}
                                </td>
                                <td className="px-4 py-2.5 text-right tabular-nums text-xs hidden md:table-cell">
                                  ${parseFloat(entry.invoiceAmount ?? "0").toFixed(2)}
                                </td>
                                <td className="px-4 py-2.5 text-right">
                                  <div className="flex flex-col items-end gap-0.5">
                                    <span className="font-semibold text-primary tabular-nums">${parseFloat(entry.commissionAmount).toFixed(2)}</span>
                                    {entry.residualCapWarning === "approaching" && (
                                      <span className="text-[10px] text-orange-500 flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Cap soon</span>
                                    )}
                                    {entry.residualCapWarning === "exhausted" && (
                                      <span className="text-[10px] text-red-500 flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />Cap reached</span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-4 py-2.5 text-center">
                                  {entry.paidAt ? (
                                    <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 text-[10px] gap-1 px-1.5 h-5">
                                      <CheckCircle2 className="w-2.5 h-2.5" />Paid
                                    </Badge>
                                  ) : (
                                    <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-400 text-[10px] gap-1 px-1.5 h-5">
                                      <Clock className="w-2.5 h-2.5" />Pending
                                    </Badge>
                                  )}
                                </td>
                                <td className="px-2 py-2.5">
                                  <button type="button" onClick={toggleExpand} className="text-muted-foreground hover:text-foreground transition-colors" data-testid={`ledger-expand-${entry.id}`}>
                                    {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                  </button>
                                </td>
                              </tr>
                              {isExpanded && (
                                <tr key={`${entry.id}-detail`} className="bg-muted/10 border-b">
                                  <td colSpan={7} className="px-4 py-3">
                                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-muted-foreground">
                                      <div><span className="font-medium text-foreground">Invoice Amt:</span> ${parseFloat(entry.invoiceAmount ?? "0").toFixed(2)}</div>
                                      <div><span className="font-medium text-foreground">Rate Type:</span> <span className="capitalize">{entry.rateType ?? "—"}</span></div>
                                      <div><span className="font-medium text-foreground">Month #:</span> {entry.monthNumber ?? "—"}</div>
                                      {entry.isPromoProtected && (
                                        <div className="flex items-center gap-1 text-purple-600 dark:text-purple-400"><Zap className="w-3 h-3" /><span>Promo-protected rate</span></div>
                                      )}
                                      {entry.paidAt && (
                                        <div><span className="font-medium text-foreground">Paid At:</span> {new Date(entry.paidAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                                      )}
                                      {entry.note && (
                                        <div><span className="font-medium text-foreground">Note:</span> {entry.note}</div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {(ledgerPageSize !== 0 && ledgerTotalPages > 1) && (
                    <div className="px-4 pb-4 flex items-center justify-between mt-3 gap-2 flex-wrap text-xs text-muted-foreground">
                      <span data-testid="ledger-pagination-info">
                        {ledgerStart}–{ledgerEnd} of {totalLedgerItems} · Page {clampedLedgerPage + 1} of {ledgerTotalPages}
                      </span>
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2" disabled={clampedLedgerPage === 0} onClick={() => setLedgerPage(clampedLedgerPage - 1)} data-testid="ledger-pagination-prev">
                          <ChevronLeft className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs px-2" disabled={clampedLedgerPage >= ledgerTotalPages - 1} onClick={() => setLedgerPage(clampedLedgerPage + 1)} data-testid="ledger-pagination-next">
                          <ChevronRight className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  )}
                  </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Payment History */}
            <Card data-testid="card-payment-history">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <History className="w-4 h-4" />Payment History
                </CardTitle>
                <CardDescription className="text-xs">Months confirmed as paid out by the platform.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {paymentHistoryLoading ? (
                  <div className="px-6 pb-6 space-y-2 pt-2">{[1,2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
                ) : paymentHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground px-6 pb-6">No confirmed payments yet. Your commissions will appear here once disbursed.</p>
                ) : (
                  <div className="divide-y">
                    {paymentHistory.map((p: any) => (
                      <div key={p.id} className="flex items-center justify-between gap-4 px-6 py-3" data-testid={`payment-history-row-${p.id}`}>
                        <div className="flex items-center gap-3">
                          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                          <div>
                            <p className="font-medium text-sm font-mono">{p.period}</p>
                            <p className="text-xs text-muted-foreground">
                              Confirmed {p.createdAt ? new Date(p.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 text-xs">Paid</Badge>
                          <span className="text-sm font-bold text-green-600 dark:text-green-400">${parseFloat(p.amountPaid).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Payment Details (Bank Account + ACH Payout History) */}
            <Card data-testid="card-payment-details">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base font-semibold flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />Payment Details
                    </CardTitle>
                    <CardDescription className="text-xs mt-1">Your ACH direct deposit bank account for commission payouts.</CardDescription>
                  </div>
                  {!bankFormEditing && !bankAccountLoading && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (bankAccount) {
                          setBankForm({
                            accountHolderName: bankAccount.accountHolderName ?? "",
                            routingNumber: "",
                            accountNumber: "",
                            accountType: (bankAccount.accountType as "checking" | "savings") ?? "checking",
                          });
                        }
                        setBankFormEditing(true);
                      }}
                      data-testid="button-edit-bank-account"
                    >
                      {bankAccount ? "Update" : "Add Account"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {bankAccountLoading ? (
                  <Skeleton className="h-20 w-full" />
                ) : bankFormEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Holder Name</label>
                      <Input
                        data-testid="input-account-holder-name"
                        placeholder="Full name on account"
                        value={bankForm.accountHolderName}
                        onChange={e => setBankForm(f => ({ ...f, accountHolderName: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Routing Number</label>
                        <Input
                          data-testid="input-routing-number"
                          placeholder="9 digits"
                          maxLength={9}
                          value={bankForm.routingNumber}
                          onChange={e => setBankForm(f => ({ ...f, routingNumber: e.target.value.replace(/\D/g, "") }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Number</label>
                        <Input
                          data-testid="input-account-number"
                          placeholder="Account number"
                          value={bankForm.accountNumber}
                          onChange={e => setBankForm(f => ({ ...f, accountNumber: e.target.value.replace(/\D/g, "") }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">Account Type</label>
                      <Select
                        value={bankForm.accountType}
                        onValueChange={v => setBankForm(f => ({ ...f, accountType: v as "checking" | "savings" }))}
                      >
                        <SelectTrigger data-testid="select-account-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="checking">Checking</SelectItem>
                          <SelectItem value="savings">Savings</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        onClick={() => saveBankAccount.mutate(bankForm)}
                        disabled={saveBankAccount.isPending || !bankForm.accountHolderName || !bankForm.routingNumber || !bankForm.accountNumber}
                        data-testid="button-save-bank-account"
                      >
                        {saveBankAccount.isPending ? "Saving…" : "Save Account"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setBankFormEditing(false)}
                        data-testid="button-cancel-bank-edit"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : bankAccount ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 p-3 border rounded-md bg-muted/30">
                      <Shield className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium" data-testid="text-bank-holder">{bankAccount.accountHolderName}</p>
                        <p className="text-xs text-muted-foreground">
                          {bankAccount.accountType === "savings" ? "Savings" : "Checking"} ·
                          Routing: <span className="font-mono" data-testid="text-bank-routing">{bankAccount.routingLast4 ?? "••••••••"}</span> ·
                          Acct: <span className="font-mono" data-testid="text-bank-account">{bankAccount.accountLast4 ?? "••••"}</span>
                        </p>
                      </div>
                      <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 text-xs shrink-0">On file</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Bank account details are encrypted and stored securely. Payouts are processed on the 15th of each month.</p>
                  </div>
                ) : (
                  <div className="text-center py-4 space-y-2">
                    <CreditCard className="w-8 h-8 text-muted-foreground mx-auto" />
                    <p className="text-sm text-muted-foreground">No bank account on file.</p>
                    <p className="text-xs text-muted-foreground">Add your bank account to receive ACH commission payouts on the 15th of each month.</p>
                  </div>
                )}

                {/* ACH Payout History */}
                {!bankAccountLoading && !bankFormEditing && (
                  <div className="pt-2 border-t space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">ACH Payout History</p>
                    {achHistoryLoading ? (
                      <Skeleton className="h-16 w-full" />
                    ) : achPayoutHistory.length === 0 ? (
                      <p className="text-xs text-muted-foreground">No ACH payouts yet.</p>
                    ) : (
                      <div className="divide-y border rounded-md overflow-hidden">
                        {achPayoutHistory.map((p: any, idx: number) => (
                          <div key={p.period ?? idx} className="flex items-center justify-between gap-3 px-3 py-2 bg-background" data-testid={`ach-payout-row-${p.period ?? idx}`}>
                            <div>
                              <p className="text-xs font-medium font-mono">{p.period}</p>
                              <p className="text-xs text-muted-foreground">{p.paidAt ? new Date(p.paidAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {p.mercuryStatus === "Initiated" ? (
                                <Badge className="bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 text-xs">Initiated</Badge>
                              ) : p.mercuryStatus === "Settled" ? (
                                <Badge className="bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400 text-xs">Settled</Badge>
                              ) : p.mercuryStatus === "Failed" ? (
                                <Badge className="bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400 text-xs">Failed</Badge>
                              ) : (
                                <Badge className="bg-muted text-muted-foreground text-xs">{p.mercuryStatus ?? "Pending"}</Badge>
                              )}
                              <span className="text-sm font-bold text-green-600 dark:text-green-400">${parseFloat(p.amountPaid ?? "0").toFixed(2)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Commission breakdown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />Dealer Commission Breakdown
                </CardTitle>
                <CardDescription className="text-xs">Per-dealer earnings this period. Cancelled dealers show $0.</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {(!data.dealerBreakdown || data.dealerBreakdown.length === 0) ? (
                  <p className="text-sm text-muted-foreground px-6 pb-6">No dealers attributed yet.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/40">
                          <th className="text-left px-4 py-3 font-medium text-muted-foreground">Dealer</th>
                          <th className="text-center px-4 py-3 font-medium text-muted-foreground">Status</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Sub Value</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Rate</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Earned</th>
                          <th className="text-right px-4 py-3 font-medium text-muted-foreground">Residual</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.dealerBreakdown.map((d: any) => (
                          <tr key={d.dealerId} className="border-b last:border-0 hover:bg-muted/20 transition-colors" data-testid={`dealer-breakdown-${d.dealerId}`}>
                            <td className="px-4 py-3 font-medium">{d.dealerName}</td>
                            <td className="px-4 py-3 text-center">{statusBadge(d.status)}</td>
                            <td className="px-4 py-3 text-right">{d.monthlyValue > 0 ? `$${d.monthlyValue}` : "—"}</td>
                            <td className="px-4 py-3 text-right text-muted-foreground">
                              {data.commissionType === "flat" ? `$${d.rateApplied}` : `${d.rateApplied}%`}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-primary">
                              {d.isActive ? `$${d.amountEarned.toFixed(2)}` : <span className="text-muted-foreground">$0.00</span>}
                            </td>
                            <td className="px-4 py-3 text-right text-xs text-muted-foreground">
                              {d.residualMonthsRemaining !== null
                                ? `Month ${d.monthsElapsed} of ${(d.monthsElapsed + d.residualMonthsRemaining)}`
                                : (data.commissionStructure === "residual" ? "∞ ongoing" : "—")}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* My Team section — visible only if user has dashboard_team permission */}
            {data?.dashboardPermissions?.dashboard_team && (myTeam || myTeamLoading || myTeamError) && (
              <div className="space-y-4" data-testid="my-team-section">
                {/* Section Header */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      <Layers className="w-5 h-5" />
                      {myTeamLoading ? "My Team" : `My Team — ${myTeam?.name ?? ""}`}
                      {teamTrend && !myTeamLoading && <OnTrackBadge isOnTrack={teamTrend.teamIsOnTrack ?? null} />}
                    </h2>
                    {myTeam && !myTeamLoading && (
                      <p className="text-xs text-muted-foreground flex items-center gap-2 mt-0.5 flex-wrap">
                        {myTeam.region && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{myTeam.region}</span>}
                        <span>{myTeam.members?.length ?? 0} member{(myTeam.members?.length ?? 0) !== 1 ? "s" : ""}</span>
                        {myTeam.description && <span>· {myTeam.description}</span>}
                        <span className="text-muted-foreground/60">Read-only view</span>
                      </p>
                    )}
                  </div>
                  {/* Period filter */}
                  <div className="flex items-center gap-1 bg-muted/40 rounded-lg p-1" data-testid="team-period-filter">
                    {(["current", "3m", "6m", "ytd"] as const).map((p) => (
                      <button
                        key={p}
                        onClick={() => setTeamPeriod(p)}
                        data-testid={`team-period-${p}`}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                          teamPeriod === p
                            ? "bg-background shadow-sm text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        {p === "current" ? "Current Month" : p === "3m" ? "3M" : p === "6m" ? "6M" : "YTD"}
                      </button>
                    ))}
                  </div>
                </div>

                {myTeamLoading ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}
                  </div>
                ) : myTeamError ? (
                  <Card data-testid="team-error-state">
                    <CardContent className="pt-6 pb-5 text-center text-muted-foreground text-sm">
                      <AlertCircle className="w-5 h-5 mx-auto mb-2 text-muted-foreground/60" />
                      Team data could not be loaded. Please try again later.
                    </CardContent>
                  </Card>
                ) : myTeam ? (
                  <>
                    {/* KPI cards row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card data-testid="team-kpi-mrr">
                        <CardContent className="pt-4 pb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" />Total Team MRR</span>
                            <DeltaBadge delta={myTeam.momDeltas?.mrr ?? null} testId="team-kpi-mrr-delta" />
                          </div>
                          <p className="text-xl font-bold" data-testid="team-kpi-mrr-value">
                            {myTeam.totalMrr == null ? "Unavailable" : `$${myTeam.totalMrr.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </p>
                        </CardContent>
                      </Card>
                      <Card data-testid="team-kpi-dealers">
                        <CardContent className="pt-4 pb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5" />Active Dealers</span>
                            <DeltaBadge delta={myTeam.momDeltas?.activeDealers ?? null} testId="team-kpi-dealers-delta" />
                          </div>
                          <p className="text-xl font-bold text-green-600 dark:text-green-400" data-testid="team-kpi-dealers-value">{myTeam.totalDealers ?? 0}</p>
                        </CardContent>
                      </Card>
                      <Card data-testid="team-kpi-override">
                        <CardContent className="pt-4 pb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" />Override Earned</span>
                            <DeltaBadge delta={myTeam.overrideCommission?.momDelta ?? null} testId="team-kpi-override-delta" />
                          </div>
                          <p className="text-xl font-bold text-primary" data-testid="team-kpi-override-value">${(myTeam.overrideCommission?.periodTotal ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                        </CardContent>
                      </Card>
                      <Card data-testid="team-kpi-attainment">
                        <CardContent className="pt-4 pb-3">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5"><Target className="w-3.5 h-3.5" />Goal Attainment</span>
                            <DeltaBadge delta={myTeam.momDeltas?.attainment ?? null} testId="team-kpi-attainment-delta" />
                          </div>
                          {myTeam.attainmentPct !== null ? (
                            <>
                              <p className="text-xl font-bold" data-testid="team-kpi-attainment-value">{myTeam.attainmentPct}%</p>
                              <Progress value={Math.min(100, myTeam.attainmentPct)} className="h-1.5 mt-1" />
                            </>
                          ) : (
                            <p className="text-xl font-bold text-muted-foreground" data-testid="team-kpi-attainment-value">—</p>
                          )}
                        </CardContent>
                      </Card>
                    </div>

                    {/* Dual-axis team trend chart */}
                    {teamTrend && (teamTrend?.teamDataPoints?.length ?? 0) < 2 && (
                      <Card data-testid="team-trend-no-data">
                        <CardContent className="flex items-center justify-center h-24 text-muted-foreground text-sm">
                          No trend data available yet — data will appear once the team has at least two months of activity.
                        </CardContent>
                      </Card>
                    )}
                    {(teamTrend?.teamDataPoints?.length ?? 0) >= 2 && (
                      <Card data-testid="team-trend-chart">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-semibold flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />Team Trend — MRR & Active Dealers
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="h-52">
                            <ResponsiveContainer width="100%" height="100%">
                              <LineChart data={teamTrend.teamDataPoints} margin={{ top: 4, right: 24, left: 4, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                                <XAxis dataKey="periodName" tick={{ fontSize: 11 }} />
                                <YAxis
                                  yAxisId="mrr"
                                  orientation="left"
                                  tick={{ fontSize: 11 }}
                                  tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`}
                                  label={{ value: "MRR ($)", angle: -90, position: "insideLeft", style: { fontSize: 10 } }}
                                />
                                <YAxis
                                  yAxisId="dealers"
                                  orientation="right"
                                  tick={{ fontSize: 11 }}
                                  allowDecimals={false}
                                  label={{ value: "Dealers", angle: 90, position: "insideRight", style: { fontSize: 10 } }}
                                />
                                <RechartsTooltip
                                  formatter={(value: any, name: string) =>
                                    name === "Total MRR" ? [`$${Number(value).toLocaleString()}`, name] : [value, name]
                                  }
                                />
                                <Legend wrapperStyle={{ fontSize: 11 }} />
                                <Line yAxisId="mrr" type="monotone" dataKey="mrr" name="Total MRR" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                                <Line yAxisId="dealers" type="monotone" dataKey="activeDealers" name="Active Dealers" stroke="hsl(142 71% 45%)" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Enhanced member table */}
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <Users className="w-4 h-4" />Team Members
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="bg-muted/40 border-b">
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground w-8">#</th>
                                <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Member</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground hidden lg:table-cell">MRR</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground hidden md:table-cell">New</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground">Active</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground hidden sm:table-cell">Quota</th>
                                <th className="text-right px-3 py-2 text-xs font-medium text-muted-foreground hidden md:table-cell">Trend</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(!myTeam.members || myTeam.members.length === 0) && (
                                <tr>
                                  <td colSpan={7} className="px-3 py-8 text-center text-sm text-muted-foreground">
                                    <Users className="w-8 h-8 text-muted-foreground/40 mx-auto mb-2" />
                                    No team members yet.
                                  </td>
                                </tr>
                              )}
                              {myTeam.members?.map((m: any) => {
                                const memberSparkline = teamTrend?.sparklines?.find((s: any) => s.id === m.id);
                                const sparkVals = memberSparkline?.dataPoints?.map((d: any) =>
                                  memberSparkline.goalType === "mrr" ? d.mrr : d.activeDealers
                                ) ?? [];
                                const rankDelta: number = m.rankDelta ?? 0;
                                return (
                                  <tr key={m.id} className="border-b last:border-0 hover:bg-muted/20" data-testid={`my-team-member-${m.id}`}>
                                    {/* Rank */}
                                    <td className="px-3 py-2.5 text-center">
                                      <div className="flex flex-col items-center gap-0.5">
                                        <span className="text-xs font-bold text-muted-foreground" data-testid={`member-rank-${m.id}`}>
                                          {m.rank <= 3
                                            ? ["🥇","🥈","🥉"][m.rank - 1]
                                            : `#${m.rank}`}
                                        </span>
                                        <span data-testid={`member-rank-delta-${m.id}`}>
                                          {rankDelta > 0
                                            ? <span className="text-green-600 dark:text-green-400 font-semibold text-xs">↑{rankDelta}</span>
                                            : rankDelta < 0
                                            ? <span className="text-red-500 dark:text-red-400 font-semibold text-xs">↓{Math.abs(rankDelta)}</span>
                                            : <span className="text-muted-foreground text-xs">—</span>}
                                        </span>
                                      </div>
                                    </td>
                                    {/* Name + badges */}
                                    <td className="px-3 py-2.5 text-xs font-medium">
                                      <div className="flex items-center gap-2 flex-wrap">
                                        <span>{m.name ?? m.username}</span>
                                        {m.id === myTeam.leaderId && (
                                          <Badge className="text-xs bg-primary/10 text-primary border-primary/20 py-0">Leader</Badge>
                                        )}
                                        {memberSparkline && <OnTrackBadge isOnTrack={memberSparkline.isOnTrack ?? null} />}
                                      </div>
                                      <span className="text-muted-foreground text-xs">{m.email ?? ""}</span>
                                    </td>
                                    {/* MRR */}
                                    <td className="px-3 py-2.5 text-right text-xs font-semibold hidden lg:table-cell" data-testid={`member-mrr-${m.id}`}>
                                      {m.mrr == null ? "Unavailable" : `$${m.mrr.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                    </td>
                                    {/* New dealers this period */}
                                    <td className="px-3 py-2.5 text-right text-xs hidden md:table-cell" data-testid={`member-new-dealers-${m.id}`}>
                                      {m.newDealers > 0 ? (
                                        <span className="font-semibold text-green-600 dark:text-green-400">+{m.newDealers}</span>
                                      ) : (
                                        <span className="text-muted-foreground">—</span>
                                      )}
                                    </td>
                                    {/* Active dealers */}
                                    <td className="px-3 py-2.5 text-right text-xs font-semibold text-green-600 dark:text-green-400" data-testid={`member-active-${m.id}`}>
                                      {m.activeDealerCount}
                                    </td>
                                    {/* Quota */}
                                    <td className="px-3 py-2.5 text-right text-xs hidden sm:table-cell" data-testid={`member-quota-${m.id}`}>
                                      {m.quotaAttainmentPct !== null
                                        ? <span className={`font-medium ${m.quotaAttainmentPct >= 100 ? "text-green-600 dark:text-green-400" : m.quotaAttainmentPct >= 70 ? "text-yellow-600 dark:text-yellow-400" : "text-red-500 dark:text-red-400"}`}>{m.quotaAttainmentPct}%</span>
                                        : <span className="text-muted-foreground">—</span>}
                                    </td>
                                    {/* Sparkline */}
                                    <td className="px-3 py-2.5 hidden md:table-cell">
                                      {sparkVals.length >= 2 && (
                                        <div className="flex justify-end">
                                          <SparklineChip
                                            data={sparkVals}
                                            isOnTrack={memberSparkline?.isOnTrack ?? null}
                                            metric={memberSparkline?.goalType === "mrr" ? "mrr" : "dealer_count"}
                                            width={72}
                                            height={28}
                                          />
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* My Override Earnings panel */}
                    <Card data-testid="override-commission-panel">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2">
                          <DollarSign className="w-4 h-4" />My Override Earnings
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Summary row */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <div className="space-y-0.5">
                            <p className="text-xs text-muted-foreground">This Period</p>
                            <p className="text-lg font-bold text-primary" data-testid="override-period-total">
                              ${(myTeam.overrideCommission?.periodTotal ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {myTeam.overrideCommission?.momDelta !== null && myTeam.overrideCommission?.momDelta !== undefined && (
                              <DeltaBadge delta={myTeam.overrideCommission.momDelta} />
                            )}
                          </div>
                          <div className="space-y-0.5">
                            <p className="text-xs text-muted-foreground">YTD Override</p>
                            <p className="text-lg font-bold" data-testid="override-ytd-total">
                              ${(myTeam.overrideCommission?.ytdTotal ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="space-y-0.5">
                            <p className="text-xs text-muted-foreground flex items-center gap-1"><CalendarClock className="w-3 h-3" />Est. Next Payout</p>
                            <p className="text-sm font-semibold" data-testid="override-next-payout">
                              {myTeam.overrideCommission?.nextPayoutDate ? fmt(myTeam.overrideCommission.nextPayoutDate) : "—"}
                            </p>
                            {(myTeam.overrideCommission?.unpaidTotal ?? 0) > 0 && (
                              <p className="text-xs text-muted-foreground">${(myTeam.overrideCommission?.unpaidTotal ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} pending</p>
                            )}
                          </div>
                        </div>

                        {/* Per-member breakdown */}
                        <div>
                          <p className="text-xs font-medium text-muted-foreground mb-2">Override by Member (this period)</p>
                          {(!myTeam.overrideCommission?.perMember || myTeam.overrideCommission.perMember.length === 0) ? (
                            <div className="text-xs text-muted-foreground py-3 text-center border rounded-md bg-muted/20">
                              No overrides recorded yet for this period.
                            </div>
                          ) : (
                            <div className="border rounded-md overflow-hidden">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-muted/40 border-b">
                                    <th className="text-left px-3 py-1.5 text-xs font-medium text-muted-foreground">Member</th>
                                    <th className="text-right px-3 py-1.5 text-xs font-medium text-muted-foreground">Override Earned</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {myTeam.overrideCommission.perMember.map((row: any) => (
                                    <tr key={row.memberId} className="border-b last:border-0 hover:bg-muted/20" data-testid={`override-member-${row.memberId}`}>
                                      <td className="px-3 py-2 text-xs font-medium">{row.memberName}</td>
                                      <td className="px-3 py-2 text-right text-xs font-semibold text-primary" data-testid={`override-amount-${row.memberId}`}>
                                        ${row.overrideTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : null}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
